import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// Mirrors functions/src/lib/types.ts. Kept in sync by hand — small surface area.

export type LicenseStatus = "active" | "suspended" | "revoked";
export type AccountStatus = "active" | "disabled";

export interface FsTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

export interface AccountDoc {
  accountId: string;
  email: string;
  name: string;
  status: AccountStatus;
  createdAt: FsTimestamp;
}

export interface LicenseDoc {
  docId?: string;
  licenseKey: string;
  accountId: string;
  restaurantId: string;
  restaurantName: string;
  status: LicenseStatus;
  expiry: FsTimestamp;
  machineId: string | null;
  machineName: string | null;
  activatedAt: FsTimestamp | null;
  lastHeartbeat: FsTimestamp | null;
  notes: string;
  features?: string[];
  createdAt: FsTimestamp;
}

// Available entitlements — keep in sync with KNOWN_FEATURES in functions/admin.ts.
export const ALL_FEATURES: ReadonlyArray<{ id: string; label: string }> = [
  { id: "pos",     label: "POS" },
  { id: "captain", label: "Captain" },
];

// Sellable plan presets. Admin picks one; the wire format is still `features[]`,
// so the backend stays generic and we can add more plans later without
// schema/function changes.
export const LICENSE_PRESETS: ReadonlyArray<{ id: string; label: string; features: string[] }> = [
  { id: "pos",         label: "POS",         features: ["pos"] },
  { id: "pos+captain", label: "POS+Captain", features: ["pos", "captain"] },
];

const sortedJoin = (xs: string[]) => [...xs].sort().join(",");

/** Match a feature set to a preset. Falls back to the first preset for
 *  legacy/unrecognized combinations so the radio always has a selection. */
export function presetFromFeatures(features: string[] | undefined | null): string {
  const key = sortedJoin(features ?? []);
  const hit = LICENSE_PRESETS.find(p => sortedJoin(p.features) === key);
  return hit?.id ?? LICENSE_PRESETS[LICENSE_PRESETS.length - 1].id;
}

const call = <In, Out>(name: string) => {
  const fn = httpsCallable<In, Out>(functions, name);
  return async (data: In): Promise<Out> => (await fn(data)).data;
};

// Admin
export const createAccount = call<
  { email: string; password: string; name: string },
  { ok: true; accountId: string }
>("createAccount");

export const listAccounts = call<void, { ok: true; accounts: AccountDoc[] }>("listAccounts");

export const updateAccount = call<
  { accountId: string; name?: string; status?: AccountStatus },
  { ok: true }
>("updateAccount");

export const resetAccountPassword = call<
  { accountId: string; newPassword: string },
  { ok: true }
>("resetAccountPassword");

export const listLicenses = call<
  { accountId?: string },
  { ok: true; licenses: LicenseDoc[] }
>("listLicenses");

// One row per license for the admin "All licenses" overview. Joined + derived
// server-side (owner email, app version, sync settings) so the page renders the
// columns directly.
export interface LicenseOverviewRow {
  docId: string;
  restaurantName: string;
  restaurantId: string;
  accountId: string;
  ownerEmail: string;
  driveEmail: string | null;
  status: LicenseStatus;
  lastReportPush: FsTimestamp | null;
  lastMasterPush: FsTimestamp | null;
  appVersion: string | null;
  reportSyncEnabled: boolean;
  requireSyncBeforeDayEnd: boolean;
  backupPath: string | null;
}
export const listLicensesOverview = call<
  void,
  { ok: true; rows: LicenseOverviewRow[] }
>("listLicensesOverview");

export const createLicense = call<
  { accountId: string; restaurantName: string; restaurantId?: string; expiryISO: string; notes?: string; features?: string[] },
  { ok: true; licenseKey: string; restaurantId: string; docId: string }
>("createLicense");

export const updateLicense = call<
  {
    licenseDocId: string;
    restaurantName?: string;
    restaurantId?: string;
    expiryISO?: string;
    features?: string[];
  },
  { ok: true }
>("updateLicense");

export const suspendLicense    = call<{ licenseDocId: string; reason?: string }, { ok: true }>("suspendLicense");
export const reactivateLicense = call<{ licenseDocId: string }, { ok: true }>("reactivateLicense");
export const reassignMachine   = call<{ licenseDocId: string }, { ok: true }>("reassignMachine");
export const setAdminClaim     = call<{ uid: string; isAdmin: boolean }, { ok: true }>("setAdminClaim");
export const bootstrapFirstAdmin = call<void, { ok: true; message: string }>("bootstrapFirstAdmin");

export interface AdminUser {
  uid: string;
  email: string;
  createdAt: string;
  lastSignIn: string;
}
export const listAdmins = call<void, { ok: true; admins: AdminUser[] }>("listAdmins");

export const lookupUserByEmail = call<
  { email: string },
  { ok: true; uid: string; email: string; isAdmin: boolean }
>("lookupUserByEmail");

export interface BackupMeta {
  project: string;
  exportedAtISO: string;
  exportedBy: string;
  version: number;
  collections: string[];
  licenseCount: number;
  accountCount: number;
  authUserCount: number;
  hasSigningKey: boolean;
}
// Returns an encrypted bundle (signing key + licenses + accounts + auth users),
// AES-256-GCM under the admin-supplied passphrase. Restore: functions/scripts/import-backup.ts.
export const exportBackup = call<
  { passphrase: string },
  { ok: true; meta: BackupMeta; encrypted: string }
>("exportBackup");

// Customer
export const getMyAccount   = call<void, { ok: true; account: AccountDoc }>("getMyAccount");
export const listMyLicenses = call<void, { ok: true; licenses: LicenseDoc[] }>("listMyLicenses");

// ── Helpers ──
export function fsTimestampToDate(ts: FsTimestamp | null | undefined): Date | null {
  if (!ts) return null;
  return new Date(ts._seconds * 1000);
}

export function formatExpiry(ts: FsTimestamp | null | undefined): string {
  const d = fsTimestampToDate(ts);
  return d ? d.toLocaleDateString() : "—";
}

/** Date + time — used for last-sync columns where the clock time matters. */
export function formatDateTime(ts: FsTimestamp | null | undefined): string {
  const d = fsTimestampToDate(ts);
  return d ? d.toLocaleString() : "—";
}
